import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { STATUS_BEZEICHNUNG } from '@asas/domain/project-status';
import type { ProjectStatus } from '@asas/domain/project-fields';
import type {
  Gewerk,
  LphGroup,
  WorkPackage,
  WorkPackageDuplikat,
  WorkPackageFachgruppeOption,
  WorkPackageLibraryEntry,
} from '@asas/domain';

import { anmeldeUrlMitRueckkehr } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc } from '../trpc';
import { AnfrageBereich, MeineAnfragen } from '../anfragen/AnfrageBereich';

import {
  darfArbeitspaketeAnlegen,
  meldungFuerArbeitspaketaktion,
  planStundenEingabe,
} from './arbeitspakete';
import type { ArbeitspaketAktion } from './arbeitspakete';
import { ArbeitspaketeAnsicht, LEERES_FREI_FORMULAR } from './ArbeitspaketeAnsicht';
import type { FreiFormular } from './ArbeitspaketeAnsicht';

export interface ArbeitspaketeBereichProps {
  readonly projekt: {
    readonly id: string;
    readonly number: string;
    readonly name: string;
    readonly status: ProjectStatus;
  };
  /** `projects:manage`: Anlegen und Plan-Stunden ändern. */
  readonly darfSchreiben: boolean;
  /** `projects:change_status`: Anlegen auch im abgeschlossenen Projekt (E7). */
  readonly darfStatus: boolean;
  readonly onSchliessen: () => void;
  /** `resource_allocation:request`: Ressourcen anonym anfragen (AC-404). */
  readonly darfAnfragen?: boolean;
}

interface Daten {
  readonly liste: readonly WorkPackage[];
  readonly bibliothek: readonly WorkPackageLibraryEntry[];
  readonly gewerke: readonly Gewerk[];
  readonly fachgruppen: readonly WorkPackageFachgruppeOption[];
  readonly lphGroups: readonly LphGroup[];
  /** Ist die Ressourcenplanung für den Mandanten aktiv (AC-404)? */
  readonly ressourcenplanung: boolean;
}

/** Warum nicht angelegt werden kann, obwohl `projects:manage` vorliegt (E7). */
export function statusHinweis(
  status: ProjectStatus,
  rechte: { readonly darfSchreiben: boolean; readonly darfStatus: boolean },
): string | null {
  if (!rechte.darfSchreiben || darfArbeitspaketeAnlegen(status, rechte)) return null;
  if (status === 'abgeschlossen') {
    return 'Das Projekt ist abgeschlossen. Arbeitspakete ergänzt hier nur, wer den Projektstatus ändern darf.';
  }
  return `Im Projektstatus „${STATUS_BEZEICHNUNG[status]}“ entstehen keine Arbeitspakete.`;
}

/**
 * Die Arbeitspakete EINES Projekts (AC-303): ansehen, aus der Bibliothek
 * übernehmen (Mehrfachauswahl, Hinweis statt stillem Duplikat), freihändig
 * anlegen, Plan-Stunden ändern.
 *
 * Gezeigt wird der Bereich erst, wenn die Person an einem Projekt bewusst
 * „Arbeitspakete“ wählt (und nur mit `ansichtSichtbar('arbeitspakete')`);
 * deshalb lädt er beim Einhängen — einmal, auch unter React StrictMode.
 *
 * Nach dem Rezept aus AC-016: Schreibzugriffe über die GETEILTE Warteschlange —
 * Anlegen je Projekt (der Server sperrt dieselbe Projektzeile, E2),
 * Plan-Stunden je Arbeitspaket —, jeder Fehler durch `klassifiziereFehler`,
 * nach Erfolg gilt die SERVERANTWORT. Ob ein Eintrag schon übernommen ist,
 * entscheidet der Server (E3); die Ansicht zeigt den Hinweis und nimmt die
 * Bestätigung je Eintrag entgegen. Nach einem 403 bleiben weitere Versuche bis
 * zum Neuladen der Seite gesperrt.
 */
export function ArbeitspaketeBereich({
  projekt,
  darfSchreiben,
  darfStatus,
  onSchliessen,
  darfAnfragen = false,
}: ArbeitspaketeBereichProps): JSX.Element {
  const [daten, setDaten] = useState<Daten | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuAnmelden, setNeuAnmelden] = useState(false);
  const [zugriffVerweigert, setZugriffVerweigert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<ReadonlySet<string>>(new Set());
  const [fachgruppeId, setFachgruppeId] = useState('');
  const [duplikate, setDuplikate] = useState<readonly WorkPackageDuplikat[] | null>(null);
  const [bestaetigt, setBestaetigt] = useState<ReadonlySet<string>>(new Set());
  const [frei, setFrei] = useState<FreiFormular>(LEERES_FREI_FORMULAR);
  const [planEingaben, setPlanEingaben] = useState<Readonly<Record<string, string>>>({});
  const [anfrageFuer, setAnfrageFuer] = useState<WorkPackage | null>(null);
  const [anfrageSendet, setAnfrageSendet] = useState(false);
  const gestartet = useRef(false);

  const rechte = { darfSchreiben, darfStatus };
  const gesperrt = laedt || sendet || zugriffVerweigert;

  function befundZeigen(befund: FehlerBefund, text: string): void {
    setFehler(text);
    setNeuAnmelden(befund.neuAnmelden);
    if (befund.art === 'zugriff') setZugriffVerweigert(true);
  }

  async function laden(): Promise<void> {
    if (zugriffVerweigert) return;
    setLaedt(true);
    setFehler(null);

    try {
      const [liste, bibliothek, gewerke, fachgruppen, settings] = await Promise.all([
        trpc.workPackages.list.query({ projectId: projekt.id }),
        trpc.workPackageLibrary.list.query(),
        trpc.workPackageLibrary.gewerke.query(),
        trpc.workPackages.fachgruppen.query(),
        trpc.tenant.settings.query(),
      ]);
      setDaten({
        liste,
        bibliothek,
        gewerke,
        fachgruppen,
        lphGroups: settings.lphGroups,
        ressourcenplanung: settings.modules.ressourcenplanung === true,
      });
      setPlanEingaben({});
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: false });
      befundZeigen(befund, befund.meldung);
    } finally {
      setLaedt(false);
    }
  }

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    void laden();
    // Einmal beim Einhängen; ein anderes Projekt bekommt einen neuen Bereich (`key`).
  }, []);

  async function schreiben<T>(
    schluessel: string,
    aktion: ArbeitspaketAktion,
    aufruf: () => Promise<T>,
  ): Promise<T | null> {
    setSendet(true);
    setFehler(null);
    setMeldung(null);
    setNeuAnmelden(false);

    try {
      return await geteilterSerialisierer.reihe(schluessel, aufruf);
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: true });
      befundZeigen(befund, meldungFuerArbeitspaketaktion(befund, aktion));
      return null;
    } finally {
      setSendet(false);
    }
  }

  function anhaengen(neue: readonly WorkPackage[]): void {
    setDaten((d) => (d === null ? d : { ...d, liste: [...d.liste, ...neue] }));
  }

  async function uebernehmen(): Promise<void> {
    if (gesperrt || auswahl.size === 0 || fachgruppeId === '') return;
    const ids = [...auswahl];
    const ergebnis = await schreiben(
      entitaetsSchluessel('project', projekt.id),
      'uebernehmen',
      () =>
        trpc.workPackages.instantiate.mutate({
          projectId: projekt.id,
          eintraege: ids.map((libraryId) => ({ libraryId, fachgruppeId })),
          zweitesExemplarFuer: ids.filter((id) => bestaetigt.has(id)),
        }),
    );

    if (ergebnis === null) return;
    if (ergebnis.ergebnis === 'hinweis') {
      setDuplikate(ergebnis.duplikate);
      return;
    }
    anhaengen(ergebnis.arbeitspakete);
    setAuswahl(new Set());
    setDuplikate(null);
    setBestaetigt(new Set());
    setMeldung(`${String(ergebnis.arbeitspakete.length)} Arbeitspaket(e) übernommen.`);
  }

  async function freiAnlegen(): Promise<void> {
    const gruppe = daten?.lphGroups[Number(frei.gruppe)];
    if (gesperrt || gruppe === undefined || fachgruppeId === '') return;
    const angelegt = await schreiben(entitaetsSchluessel('project', projekt.id), 'anlegen', () =>
      trpc.workPackages.createFrei.mutate({
        projectId: projekt.id,
        name: frei.name,
        gewerkId: frei.gewerkId,
        leistungsphasen: [...gruppe.phases],
        fachgruppeId,
        planHours: planStundenEingabe(frei.planHours),
      }),
    );

    if (angelegt === null) return;
    anhaengen([angelegt]);
    setFrei(LEERES_FREI_FORMULAR);
    setMeldung(`„${angelegt.name}“ angelegt.`);
  }

  async function planSpeichern(arbeitspaket: WorkPackage): Promise<void> {
    const eingabe = planEingaben[arbeitspaket.id];
    if (gesperrt || eingabe === undefined) return;
    const geaendert = await schreiben(
      entitaetsSchluessel('workPackage', arbeitspaket.id),
      'planstunden',
      () =>
        trpc.workPackages.updatePlanHours.mutate({
          id: arbeitspaket.id,
          version: arbeitspaket.version,
          planHours: planStundenEingabe(eingabe),
        }),
    );

    if (geaendert === null) return;
    setDaten((d) =>
      d === null
        ? d
        : { ...d, liste: d.liste.map((ap) => (ap.id === geaendert.id ? geaendert : ap)) },
    );
    setPlanEingaben((alt) =>
      Object.fromEntries(Object.entries(alt).filter(([id]) => id !== geaendert.id)),
    );
    setMeldung(`Plan-Stunden für „${geaendert.name}“ gespeichert.`);
  }

  return (
    <section aria-label={`Arbeitspakete ${projekt.number}`}>
      <h3>
        Arbeitspakete · {projekt.number} {projekt.name}
      </h3>
      <button disabled={gesperrt} onClick={() => void laden()}>
        Neu laden
      </button>{' '}
      <button disabled={sendet} onClick={onSchliessen}>
        Schließen
      </button>
      <div role="status" aria-live="polite">
        {laedt && <p>Arbeitspakete werden geladen …</p>}
        {meldung !== null && <p>{meldung}</p>}
      </div>
      {fehler !== null && <p role="alert">{fehler}</p>}
      {neuAnmelden && <a href={anmeldeUrlMitRueckkehr('/')}>Erneut anmelden</a>}
      {daten !== null && (
        <ArbeitspaketeAnsicht
          liste={daten.liste}
          bibliothek={daten.bibliothek}
          gewerke={daten.gewerke}
          fachgruppen={daten.fachgruppen}
          lphGroups={daten.lphGroups}
          darfAnlegen={darfArbeitspaketeAnlegen(projekt.status, rechte)}
          darfPlanStunden={darfSchreiben}
          statusHinweis={statusHinweis(projekt.status, rechte)}
          gesperrt={gesperrt}
          auswahl={auswahl}
          fachgruppeId={fachgruppeId}
          duplikate={duplikate}
          bestaetigt={bestaetigt}
          frei={frei}
          planEingaben={planEingaben}
          onAuswahl={(libraryId, gewaehlt) => {
            setAuswahl((alt) => {
              const neu = new Set(alt);
              if (gewaehlt) neu.add(libraryId);
              else neu.delete(libraryId);
              return neu;
            });
          }}
          onFachgruppe={setFachgruppeId}
          onBestaetigung={(libraryId, ja) => {
            setBestaetigt((alt) => {
              const neu = new Set(alt);
              if (ja) neu.add(libraryId);
              else neu.delete(libraryId);
              return neu;
            });
          }}
          onUebernehmen={() => void uebernehmen()}
          onFrei={setFrei}
          onFreiAnlegen={() => void freiAnlegen()}
          onPlanEingabe={(id, wert) => {
            setPlanEingaben((alt) => ({ ...alt, [id]: wert }));
          }}
          onPlanSpeichern={(ap) => void planSpeichern(ap)}
          darfAnfragen={darfAnfragen && daten.ressourcenplanung}
          onAnfragen={setAnfrageFuer}
          anfragenGesperrt={anfrageSendet}
        />
      )}
      {darfAnfragen && daten?.ressourcenplanung === true && (
        <>
          {anfrageFuer !== null && (
            <AnfrageBereich
              key={anfrageFuer.id}
              projectId={projekt.id}
              arbeitspaket={anfrageFuer}
              fachgruppen={daten.fachgruppen}
              onSchliessen={() => {
                setAnfrageFuer(null);
              }}
              onSendet={setAnfrageSendet}
            />
          )}
          <MeineAnfragen />
        </>
      )}
    </section>
  );
}
